import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { privacyPolicyData as data } from "../data/candidate-explainer-privacy-policy.data";
import { useRedirectUrl } from "../hooks/useRedirectUrl";
import styles from "./candidate-explainer.module.scss";

export function AcceptableUsePolicy() {
  const redirect = useRedirectUrl();

  return (
    <div className={`${styles["candidateExplainer"]} candidate-container`}>
      <Helmet>
        <title>Sapia | Privacy Policy</title>
      </Helmet>
      <div className={styles["explainerBanner"]}>
        <ul className={styles["landingBreadcrumbs"]}>
          {data.header_section.breadcrums.map((b, i) => {
            switch (i) {
              case 0:
                return (
                  <li className={styles["candidateBtn"]} key={i}>
                    <a href={redirect}>{b.label}</a>
                  </li>
                );
              default:
                return (
                  <li key={i}>
                    <Link to={b.link}>{b.label}</Link>
                  </li>
                );
            }
          })}
        </ul>
      </div>

      <div className="container">
        <h1>
          <strong style={{ color: "#faa5fa" }}>
            Acceptable Use Policy for Sapia
          </strong>
        </h1>

        <h2 id="1-application-of-this-policy">1. Application of this Policy</h2>
        <p>
          This Acceptable Use Policy (&quot;Policy&quot;) sets out the policy of
          Sapia Pty Ltd, Sapia Limited and our affiliates
          (&quot;we&quot;, &quot;us&quot;, &quot;our&quot;) regarding access to
          and use of:
        </p>
        <ul>
          <li>
            the hosted services provided or made available by us as a service
            via the internet (&quot;Services&quot;); and
          </li>
          <li>
            the online platform managed by us and used by us to provide the
            Services (&quot;SaaS Platform&quot;).
          </li>
        </ul>
        <p>
          This Policy applies to you if you access the SaaS Platform and/or use
          the Services. Your failure to comply with this Policy may lead to
          suspension or termination of the Services and/or restricted access to
          the SaaS Platform.
        </p>
        <p>
          The terms of this Policy apply in addition to and not instead of those
          contained in your Sapia SaaS Agreement
          (&quot;Agreement&quot;). Compliance with this Policy is a condition of
          the Agreement.
        </p>
        <p>
          We may vary this Policy at any time by placing a notice on our website
          available at www.predictivehire.com. If you access the SaaS Platform
          and/or use the Services, your access and/or use will constitute
          acceptance of the terms of this Policy as published on the website at
          that time.
        </p>
        <h2 id="2-authorised-use">2. Authorised use</h2>
        <p>
          Only your employees, agents or independent contractors who are
          authorised by you (&quot;Authorised Users&quot;) may use the SaaS
          Platform and Services. You agree that you are at all times responsible
          for any access credentials (including usernames and passwords) which
          we issue to you or your Authorised Users, including for ensuring that
          no one other than your Authorised Users has access to such access
          credentials.
        </p>
        <p>
          You agree that you will only use the SaaS Platform and Services in
          accordance with the Agreement and will comply with all provisions of
          the Agreement in addition to the provisions of this Policy.
        </p>

        <h2 id="3-unauthorised-use">3. Unauthorised use</h2>
        <p>You must NEVER:</p>
        <ol type="a">
          <li>
            permit any person who is not an Authorised Person to access or use
            the Services, or use the Services to provide services to third
            parties;
          </li>
          <li>make or attempt to make any alteration to the SaaS Platform;</li>
          <li>
            use the Services in any way that causes, or may cause, damage to the
            Services or the SaaS Platform or impairment of the availability or
            accessibility of the Services;
          </li>
          <li>
            use the Services in a manner that is unlawful, harmful, illegal,
            fraudulent, discriminatory or otherwise improper, or in connection
            with any unlawful, harmful, illegal, fraudulent, discriminatory or
            otherwise improper purpose or activity;
          </li>
          <li>
            attempt to copy, modify, duplicate, create derivative works from,
            frame, mirror, republish, download, display, transmit, or distribute
            all or any portion of the SaaS Platform (including any SaaS Platform
            software) or documentation relating to the SaaS Platform or Services
            which we provide or make available to you from time to time
            (&quot;Documentation&quot;) in any form or media or by any means;
          </li>
          <li>
            use any robot, spider, retrieval application, or other manual or
            automatic device or process to retrieve, index, data mine, harvest,
            scrape or in any way reproduce or circumvent the navigational
            structure or presentation of the SaaS Platform or its content,
            including any data, software code information or metadata.
          </li>
          <li>
            attempt to reverse compile, disassemble, reverse engineer or
            otherwise reduce to human-perceivable form all or any part of the
            SaaS Platform (including any SaaS Platform software);
          </li>
          <li>
            access all or any part of the Services or Documentation in order to
            build a product or service which competes with the Services;
          </li>
          <li>
            license, sell, rent, lease, transfer, assign, distribute, display,
            disclose, or otherwise commercially exploit, or otherwise make the
            Services or Documentation available to any third party except the
            Authorised Users;
          </li>
          <li>
            during the course of its use of the Services, access, store,
            distribute or transmit any viruses or any material that:{" "}
            <ol type="i">
              <li>
                is unlawful, harmful, threatening, defamatory, or obscene;
              </li>
              <li>facilitates illegal activity; </li>
              <li>depicts sexually explicit images;</li>
              <li>
                is discriminatory on the basis of (without limitation) race,
                gender, colour, religious belief, sexual orientation,
                disability; or
              </li>
              <li>
                is otherwise illegal or causes damage or injury to any person or
                property;
              </li>
            </ol>
          </li>
          <li>
            attempt to access the software code (including object code,
            intermediate code and source code) of the SaaS Platform.
          </li>
          <li>
            permit other individuals or entities to copy or otherwise access any
            findings, observations or understandings which are generated by the
            SaaS Platform using data provided by you, your Authorised Users or
            an applicant (referred to as Data Driven Insights);
          </li>
          <li>
            attempt to probe or test the accuracy or vulnerability of the Data
            Driven Insights or any other aspect of the Services;
          </li>
          <li>
            interfere or attempt to interfere with the ability of any other
            Authorised User to access, use or enjoy the Services or SaaS
            Platform; or
          </li>
          <li>use the Services to mislead or deceive anyone.</li>
        </ol>

        <div className="candidate-inner-padding"></div>
      </div>

      <div className="interview-support-section explainer-padding">
        <div className="container">
          <div className="text-center">
            <h3 className="blue-title">{data.contact_section.section_title}</h3>
            <button
              type="button"
              className="btn-help"
              style={{ cursor: "pointer" }}
            >
              {data.contact_section.button_label}
            </button>
            <p>
              <a
                href={data.contact_section.privacy_button_link}
                className="black-link"
              >
                {data.contact_section.privacy_button_label}
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
